import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Users, Tag, Ticket, CreditCard, Download, LogIn, Clock, Car, Info, Award, User, ExternalLink, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import usePageMeta from '@/hooks/usePageMeta';
import Linkify from '@/lib/linkify';
import LinkPreviews from '@/components/LinkPreviews';
import RichText from '@/components/RichText';
import MemberProfileModal from '@/components/MemberProfileModal';
import PageSideRail from '@/components/PageSideRail';
import { SponsorBanner } from '@/components/SponsorBanner';
import {
  AttendeeAvatar, SpeakerCard, AgendaTimeline,
  formatEventDate, formatEventFormat,
} from '@/components/EventDetailParts';

const BACKEND_URL = '';

/**
 * EventDetailPage — full-page event view at /events/:eventId, replacing the
 * old in-page modal. Has its own URL (deep-linkable / shareable) and a
 * banner-graphic hero area that matches the styling of other inner pages.
 *
 * Fetches the event from the public list endpoint and surfaces the same
 * content the old modal showed: description, agenda, speakers, "Recommended
 * to meet" pairs, parking, additional info, sponsors, RSVP/Pay actions.
 */
export default function EventDetailPage({ user: userProp }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [meetRecs, setMeetRecs] = useState([]);
  const [meetRecsLoading, setMeetRecsLoading] = useState(false);
  const [user, setUser] = useState(userProp || null);

  // Same self-hydration approach as EventsPage so deep links work for
  // already-signed-in members who land here as their first interaction.
  useEffect(() => {
    if (userProp) { setUser(userProp); return; }
    const cached = sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user');
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch { /* ignore */ }
    }
    fetch(`${BACKEND_URL}/api/auth/session`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401) {
          ['sll_user', 'session_token'].forEach(k => sessionStorage.removeItem(k));
          ['sll_user', 'sll_session_token'].forEach(k => localStorage.removeItem(k));
          setUser(null);
        }
        return r.ok ? r.json() : null;
      })
      .then(freshUser => {
        if (freshUser) {
          setUser(freshUser);
          const s = JSON.stringify(freshUser);
          sessionStorage.setItem('sll_user', s);
          localStorage.setItem('sll_user', s);
        }
      })
      .catch(() => {});
  }, [userProp]);

  const isLoggedIn = !!user;
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  usePageMeta(event ? {
    title: event.title,
    description: event.description?.slice(0, 200),
    image: event.banner_url || event.image,
  } : { title: 'Event' });

  const loadEvent = () => {
    fetch(`${BACKEND_URL}/api/events`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(list => {
        const found = (Array.isArray(list) ? list : []).find(e => e.event_id === eventId);
        if (!found) { setNotFound(true); setLoading(false); return; }
        setEvent(found);
        setLoading(false);
      })
      .catch(() => { setLoading(false); setNotFound(true); });
  };

  useEffect(() => { loadEvent(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recommended-to-meet only loads for registered attendees
  useEffect(() => {
    if (!event || !isLoggedIn || !event.my_registration) { setMeetRecs([]); return; }
    setMeetRecsLoading(true);
    fetch(`${BACKEND_URL}/api/events/${event.event_id}/meet-recommendations`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : { recommendations: [] })
      .then(d => setMeetRecs(d.recommendations || []))
      .catch(() => setMeetRecs([]))
      .finally(() => setMeetRecsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.event_id, isLoggedIn]);

  const handleRSVP = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${event.event_id}/rsvp`, { method: 'POST', credentials: 'include', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'RSVP failed');
      if (data.requires_payment) {
        toast.success(`Registered! Click "Pay ${(data.ticket_currency || 'GBP').toUpperCase()} ${Number(data.ticket_price).toFixed(2)}" to confirm your ticket.`);
      } else {
        toast.success(data.ticket_id ? `Registered! Badge sent to your email. Ticket: ${data.ticket_id}` : 'RSVP confirmed!');
      }
      loadEvent();
    } catch (e) { toast.error(e.message || 'Failed to RSVP.'); }
  };

  const handlePay = async () => {
    try {
      toast.info('Redirecting to secure payment…');
      const res = await fetch(`${BACKEND_URL}/api/events/${event.event_id}/checkout`, { method: 'POST', credentials: 'include', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not create checkout session');
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      throw new Error('No checkout URL returned');
    } catch (e) { toast.error(e.message || 'Payment could not be started.'); }
  };

  const handleAgreeToMeet = (rec) => navigate(`/messages?to=${rec.user_id}`);
  const handleDismissMeet = async (rec) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${event.event_id}/meet-recommendations/${rec.user_id}/dismiss`,
        { method: 'POST', credentials: 'include', headers });
      if (!res.ok) throw new Error();
      setMeetRecs(prev => prev.filter(r => r.user_id !== rec.user_id));
    } catch { toast.error('Could not dismiss — try again'); }
  };

  const downloadIcal = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${event.event_id}/ical`, { credentials: 'include', headers });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${event.title.replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/ /g, '_')}.ics`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Calendar event downloaded!');
    } catch { toast.error('Failed to download calendar event'); }
  };

  const RSVPAction = () => {
    if (!event) return null;
    if (!isLoggedIn) {
      return (
        <Button onClick={() => navigate(`/login?next=${encodeURIComponent(`/events/${event.event_id}`)}`)} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="event-detail-signin-cta">
          <LogIn className="h-4 w-4 mr-2" /> Sign in to register
        </Button>
      );
    }
    const reg = event.my_registration;
    const isPaid = event.ticket_price > 0;
    const ccy = (event.ticket_currency || 'GBP').toUpperCase();
    const priceLabel = isPaid ? `${ccy} ${Number(event.ticket_price).toFixed(2)}` : '';
    if (reg && reg.payment_status !== 'pending') {
      return (
        <Button disabled className="bg-brand-sage text-white opacity-90" data-testid="event-detail-registered">
          <Ticket className="h-4 w-4 mr-2" /> Registered{isPaid ? ' · Paid' : ''}
        </Button>
      );
    }
    if (reg && reg.payment_status === 'pending') {
      return (
        <Button onClick={handlePay} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="event-detail-pay-btn">
          <CreditCard className="h-4 w-4 mr-2" /> Pay {priceLabel}
        </Button>
      );
    }
    return (
      <Button onClick={handleRSVP} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="event-detail-rsvp-btn">
        {isPaid ? <><Ticket className="h-4 w-4 mr-2" /> Register · {priceLabel}</> : 'RSVP Now'}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-4xl animate-pulse">
            <div className="h-48 bg-slate-200 rounded-2xl mb-6" />
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-3" />
            <div className="h-4 bg-slate-100 rounded w-1/2" />
          </div>
        </main>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-2xl text-center">
            <p className="text-slate-500 mb-4">We couldn&apos;t find that event. It may have been removed or the link is wrong.</p>
            <Button onClick={() => navigate('/events')} variant="outline"><ArrowLeft className="h-4 w-4 mr-2" /> Back to events</Button>
          </div>
        </main>
      </div>
    );
  }

  const banner = event.banner_url || event.image;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-20 lg:pt-24 pb-12 px-3 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col xl:flex-row gap-6">
            <div className="flex-1 min-w-0">
              <button onClick={() => navigate('/events')}
                className="text-sm text-brand-navy hover:underline mb-3 inline-flex items-center gap-1"
                data-testid="event-detail-back-btn">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to events
              </button>

              {/* Banner hero — uses the event's banner image if set, otherwise
                  falls back to the brand gradient so the page still has the
                  expected hero treatment that every other inner page has. */}
              <Card className="overflow-hidden border-0 shadow-lg mb-6" data-testid="event-detail-banner">
                <div
                  className={`relative ${banner ? '' : 'bg-gradient-to-br from-brand-navy via-brand-navy to-brand-sage'}`}
                  style={banner ? {
                    backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.75) 100%), url(${banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : undefined}
                >
                  <div className="p-6 md:p-10 text-white min-h-[180px] md:min-h-[240px] flex flex-col justify-end">
                    {event.format && (
                      <span className="inline-block px-2.5 py-0.5 bg-white/20 backdrop-blur-sm rounded text-[11px] font-semibold uppercase tracking-wider mb-3 w-fit"
                        data-testid="event-detail-format-pill">
                        {formatEventFormat(event.format)}
                      </span>
                    )}
                    <h1 className="text-2xl md:text-4xl font-bold font-heading mb-3 drop-shadow-sm" data-testid="event-detail-title">
                      {event.title}
                    </h1>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm md:text-base text-white/90">
                      <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatEventDate(event.date)}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {event.location}</span>
                      {event.max_delegates ? (
                        <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {(event.rsvps?.length || 0)} / {event.max_delegates}</span>
                      ) : (
                        <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {(event.rsvps?.length || 0)} registered</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Sticky action bar */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="event-detail-actions">
                <RSVPAction />
                <Button variant="outline" onClick={downloadIcal} data-testid="event-detail-ical-btn">
                  <Download className="h-4 w-4 mr-2" /> Add to calendar
                </Button>
              </div>

              <Card className="p-5 md:p-7 border-0 shadow-sm space-y-7" data-testid="event-detail-body">
                {event.description && (
                  <section data-testid="event-detail-description">
                    <RichText html={event.description} className="text-slate-700 leading-relaxed" />
                    <LinkPreviews text={event.description} />
                  </section>
                )}

                {event.topic_tags?.length > 0 && (
                  <div className="flex items-center flex-wrap gap-2">
                    <Tag className="h-4 w-4 text-brand-navy" />
                    {event.topic_tags.map((t, i) => (
                      <span key={i} className="px-2.5 py-1 bg-brand-navy/5 text-brand-navy text-xs rounded-full">{t}</span>
                    ))}
                  </div>
                )}

                {(event.agenda?.length > 0 || event.agenda_intro) && (
                  <section data-testid="event-detail-agenda">
                    <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                      <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        <Clock className="h-5 w-5 text-brand-navy" /> Agenda
                      </h3>
                      <button
                        type="button"
                        onClick={() => navigate(`/events/${event.event_id}/agenda`)}
                        data-testid="open-full-agenda-btn"
                        className="text-base font-bold text-white bg-gradient-to-r from-brand-navy to-brand-sage hover:opacity-90 transition-opacity rounded-full px-4 py-1.5 inline-flex items-center gap-2 shadow-sm">
                        Book Sessions Here <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                    {event.agenda_intro && (
                      <RichText html={event.agenda_intro} className="text-sm text-slate-600 mb-4" testId="event-agenda-intro" />
                    )}
                    <AgendaTimeline agenda={event.agenda} onViewSpeaker={(spk) => setViewingMember(spk)} />
                  </section>
                )}

                {event.speakers_detail?.length > 0 && (
                  <section data-testid="event-detail-speakers">
                    <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <User className="h-5 w-5 text-brand-navy" /> Speakers
                    </h3>
                    <div className="space-y-3">
                      {event.speakers_detail.map((spk, idx) => (
                        <SpeakerCard key={idx} speaker={spk} onViewProfile={(s) => setViewingMember({ user_id: s.user_id, name: s.name, picture: s.headshot_url, bio: s.bio })} />
                      ))}
                    </div>
                  </section>
                )}

                {event.my_registration && (meetRecsLoading || meetRecs.length > 0) && (
                  <section data-testid="event-detail-meet-recs"
                    className="rounded-xl border-2 border-brand-sage/30 bg-gradient-to-br from-brand-sage/5 to-transparent p-5">
                    <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
                      <Users className="h-5 w-5 text-brand-sage" /> Recommended to meet on the day
                    </h3>
                    <p className="text-xs text-slate-500 mb-4">Other attendees whose interests overlap with yours.</p>
                    {meetRecsLoading ? (
                      <p className="text-sm text-slate-400 italic">Looking for matches…</p>
                    ) : (
                      <div className="space-y-3">
                        {meetRecs.map(rec => (
                          <div key={rec.user_id} data-testid={`meet-rec-${rec.user_id}`}
                            className="flex flex-col sm:flex-row gap-3 sm:items-center p-3 bg-white rounded-lg border border-slate-100 shadow-sm">
                            <button type="button" onClick={() => setViewingMember(rec)}
                              className="flex items-center gap-3 min-w-0 flex-1 text-left"
                              data-testid={`meet-rec-open-${rec.user_id}`}>
                              <AttendeeAvatar attendee={rec} size="md" />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate">{rec.name}</p>
                                {(rec.role || rec.organisation) && (
                                  <p className="text-xs text-slate-500 truncate">
                                    {[rec.role, rec.organisation].filter(Boolean).join(' · ')}
                                  </p>
                                )}
                                {rec.reasons?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {rec.reasons.map((r, i) => (
                                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-brand-sage/10 text-brand-sage border border-brand-sage/20">
                                        {r}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </button>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" onClick={() => handleAgreeToMeet(rec)}
                                data-testid={`meet-rec-yes-${rec.user_id}`}
                                className="bg-brand-sage hover:bg-brand-sage/90 text-white">
                                Yes — message
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDismissMeet(rec)}
                                data-testid={`meet-rec-no-${rec.user_id}`}>
                                No thanks
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {event.parking_info && (
                  <section data-testid="event-detail-parking">
                    <h3 className="text-base font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Car className="h-5 w-5 text-brand-navy" /> Parking Information
                    </h3>
                    <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{event.parking_info}</div>
                  </section>
                )}

                {event.additional_info && (
                  <section data-testid="event-detail-additional">
                    <h3 className="text-base font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Info className="h-5 w-5 text-brand-navy" /> Additional Information
                    </h3>
                    <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{event.additional_info}</div>
                  </section>
                )}

                {event.event_sponsors?.length > 0 && (
                  <section data-testid="event-detail-sponsors">
                    <h3 className="text-base font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5 text-brand-navy" /> Event Sponsors
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      {event.event_sponsors.map((sp, idx) => (
                        <a key={idx} href={sp.website || '#'} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                          data-testid={`event-detail-sponsor-${idx}`}>
                          {sp.logo_url ? (
                            <img src={sp.logo_url} alt={sp.name} className="w-10 h-10 object-contain" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><Award className="h-5 w-5 text-slate-400" /></div>
                          )}
                          <div>
                            <span className="text-sm font-semibold text-slate-700">{sp.name}</span>
                            {sp.website && <ExternalLink className="inline h-3 w-3 text-slate-400 ml-1.5" />}
                          </div>
                        </a>
                      ))}
                    </div>
                  </section>
                )}

                {/* Bottom RSVP action - keeps the CTA reachable on long pages */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                  <RSVPAction />
                </div>
              </Card>

              <SponsorBanner placement="events-descriptor" />
            </div>

            <PageSideRail placement="events-descriptor" />
          </div>
        </div>
      </main>

      <MemberProfileModal
        member={viewingMember}
        open={!!viewingMember}
        onClose={() => setViewingMember(null)}
        user={user}
      />
    </div>
  );
}
